import React, { useState, useEffect } from 'react';
import { Check, Zap, Rocket, TrendingUp, Building, ArrowRight, Loader2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import { paymentsAPI } from '../services/api';

const plans = [
  {
    id: 'starter',
    name: 'Starter',
    price: 15,
    description: 'Great for quick experimentations',
    costPerMin: 0.105,
    minutes: 143,
    extraUsage: 0.009,
    knowledgeBase: '5 MB',
    icon: Zap,
    color: 'from-blue-500 to-blue-600',
    popular: false
  },
  {
    id: 'jump_starter',
    name: 'Jump Starter',
    price: 30,
    description: 'Best for building and sharing voice AI demos',
    costPerMin: 0.095,
    minutes: 316,
    extraUsage: 0.008,
    knowledgeBase: '10 MB',
    icon: Rocket,
    color: 'from-teal-500 to-teal-600',
    popular: true
  },
  {
    id: 'early_deployers',
    name: 'Early Deployers',
    price: 36,
    originalPrice: 40,
    discount: '10% OFF',
    description: 'Best for users doing a POC with live voice AI',
    costPerMin: 0.085,
    minutes: 471,
    extraUsage: 0.006,
    knowledgeBase: '50 MB',
    icon: TrendingUp,
    color: 'from-purple-500 to-purple-600',
    popular: false
  },
  {
    id: 'growth',
    name: 'Growth',
    price: 200,
    description: 'Best for users scaling post-POC voice AI usage',
    costPerMin: 0.070,
    minutes: 2857,
    extraUsage: 0.005,
    knowledgeBase: '100 MB',
    icon: Building,
    color: 'from-orange-500 to-orange-600',
    popular: false
  }
];

const features = [
  { name: 'Voice AI Agents', all: true },
  { name: 'Test Calls', all: true },
  { name: 'Call Analytics', all: true },
  { name: 'Knowledge Base', all: true },
  { name: 'API Access', all: true },
  { name: 'Call Transfer', all: true },
  { name: 'Noise Reducer', all: true },
  { name: 'One-click Integrations', all: true },
  { name: 'Email Support', all: true },
];

const Pricing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { endUser } = useAuth();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check for cancelled payment
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'cancelled') {
      setError('Payment was cancelled. You can try again when ready.');
    }
  }, [searchParams]);

  const handleSelectPlan = async (planId) => {
    // If user is not logged in, redirect to signup
    if (!endUser) {
      localStorage.setItem('selectedPlan', planId);
      navigate('/signup');
      return;
    }

    // If user is logged in, initiate Stripe checkout
    setLoadingPlan(planId);
    setError('');
    
    try {
      const response = await paymentsAPI.createCheckout({
        plan_id: planId,
        origin_url: window.location.origin,
        user_id: endUser.id
      });

      if (response.data.success && response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.detail || 'Failed to initiate payment. Please try again.');
      setLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <div className="max-w-7xl mx-auto px-6 text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Simple, Transparent <span className="text-teal-400">Pricing</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Choose the perfect plan for your business. Start free, scale as you grow, and only pay for what you use.
          </p>
          
          {/* Error Message */}
          {error && (
            <div className="mt-4 max-w-md mx-auto bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
          
          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <span className={`text-sm ${billingCycle === 'monthly' ? 'text-white' : 'text-gray-500'}`}>Monthly</span>
            <button
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
              className="relative w-14 h-7 bg-[#1a1a1f] rounded-full border border-gray-700 transition-colors"
            >
              <div className={`absolute top-1 w-5 h-5 bg-teal-400 rounded-full transition-transform ${
                billingCycle === 'yearly' ? 'translate-x-7' : 'translate-x-1'
              }`} />
            </button>
            <span className={`text-sm ${billingCycle === 'yearly' ? 'text-white' : 'text-gray-500'}`}>
              Yearly <span className="text-teal-400">(Save 20%)</span>
            </span>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`relative bg-[#0d1117] border rounded-2xl p-6 transition-all duration-300 hover:scale-105 ${
                  plan.popular ? 'border-teal-400 shadow-lg shadow-teal-400/20' : 'border-gray-800 hover:border-gray-700'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-teal-400 text-black text-xs font-bold rounded-full">
                    MOST POPULAR
                  </div>
                )}
                
                {plan.discount && (
                  <div className="absolute -top-3 right-4 px-2 py-1 bg-purple-500 text-white text-xs font-bold rounded-full">
                    {plan.discount}
                  </div>
                )}

                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${plan.color} flex items-center justify-center mb-4`}>
                  <plan.icon className="w-6 h-6 text-white" />
                </div>

                <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                <p className="text-gray-500 text-sm mb-4">{plan.description}</p>

                <div className="mb-6">
                  {plan.originalPrice && (
                    <span className="text-gray-500 line-through text-lg mr-2">${plan.originalPrice}</span>
                  )}
                  <span className="text-4xl font-bold text-white">
                    ${billingCycle === 'yearly' ? Math.round(plan.price * 0.8) : plan.price}
                  </span>
                  <span className="text-gray-500">/month</span>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Cost per minute</span>
                    <span className="text-white font-medium">${plan.costPerMin}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Included minutes</span>
                    <span className="text-teal-400 font-medium">~{plan.minutes} min</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Extra usage</span>
                    <span className="text-white font-medium">+${plan.extraUsage}/min</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Knowledge base</span>
                    <span className="text-white font-medium">{plan.knowledgeBase}</span>
                  </div>
                </div>

                <button
                  onClick={() => handleSelectPlan(plan.id)}
                  disabled={loadingPlan === plan.id}
                  className={`w-full py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${
                    plan.popular
                      ? 'bg-teal-400 hover:bg-teal-500 text-black'
                      : 'bg-[#1a1a1f] hover:bg-[#252530] text-white border border-gray-700'
                  } disabled:opacity-70`}
                  data-testid={`select-plan-${plan.id}`}
                >
                  {loadingPlan === plan.id ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Processing...
                    </>
                  ) : endUser ? (
                    <>
                      Upgrade Now
                      <ArrowRight className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      Get Started
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>

          {/* Enterprise Card */}
          <div className="mt-8 bg-gradient-to-r from-[#0d1117] to-[#1a1a1f] border border-gray-800 rounded-2xl p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">Enterprise</h3>
                <p className="text-gray-400">
                  Launch at scale with volume-based discounts. As low as <span className="text-teal-400 font-semibold">$0.04/min</span>
                </p>
                <div className="flex flex-wrap gap-4 mt-4">
                  <span className="flex items-center gap-2 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-teal-400" />
                    Agent Training from Recording
                  </span>
                  <span className="flex items-center gap-2 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-teal-400" />
                    Dedicated Support
                  </span>
                  <span className="flex items-center gap-2 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-teal-400" />
                    Custom Integrations
                  </span>
                </div>
              </div>
              <button className="bg-white hover:bg-gray-100 text-black px-8 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap">
                Contact Sales
              </button>
            </div>
          </div>
        </div>

        {/* Features Comparison */}
        <div className="max-w-7xl mx-auto px-6 mt-20">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            All Plans Include
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center gap-3 bg-[#0d1117] border border-gray-800 rounded-lg p-4">
                <Check className="w-5 h-5 text-teal-400 flex-shrink-0" />
                <span className="text-gray-300 text-sm">{feature.name}</span>
              </div>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Pricing;
